import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const feeTypeKeys = {
  all: ['feeTypes'],
  lists: () => [...feeTypeKeys.all, 'list'],
  details: () => [...feeTypeKeys.all, 'detail'],
  detail: (id) => [...feeTypeKeys.details(), id],
};

export const useFeeTypes = () => {
  return useQuery({
    queryKey: feeTypeKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('fee_types');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useFeeType = (id, options = {}) => {
  return useQuery({
    queryKey: feeTypeKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`fee_types/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options
  });
};

export const useCreateFeeType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (feeTypeData) => {
      const response = await apiClient.post('fee_types', feeTypeData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feeTypeKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating fee type:', error);
    }
  });
};

export const useUpdateFeeType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`fee_types/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(feeTypeKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: feeTypeKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating fee type:', error);
    }
  });
};

export const useDeleteFeeType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`fee_types/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: feeTypeKeys.lists() });
      queryClient.removeQueries({ queryKey: feeTypeKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting fee type:', error);
    }
  });
};